import React, { useMemo, useState } from "react";
import { colors } from "../../styles/colors";
import { MONTH_NAMES } from "../../constants/calendarLabels";
import { dateToISO, isSameMonth } from "../../utils/date";

const YearGrid = ({
  year,
  allScheduled,
  dragId,
  dropTarget,
  setDropTarget,
  onDrop,
  onRemove,
  onDragStart,
  onDragEnd,
  onOpenMilestone,
  executeMode = false,
  onMarkDone,
}: any) => {
  const months = Array.from({ length: 12 }, (_, index) => new Date(year, index, 1));
  const [openMonth, setOpenMonth] = useState<number>(() => new Date().getMonth());
  const [expandedMonths, setExpandedMonths] = useState<Record<number, boolean>>({});

  const itemsByMonth = useMemo(() => {
    const result = {};
    for (let index = 0; index < 12; index++) result[index] = [];
    allScheduled.forEach(({ iso, milestone, goal, dream }) => {
      const dateValue = new Date(`${iso}T00:00:00`);
      if (dateValue.getFullYear() !== year) return;
      result[dateValue.getMonth()].push({ iso, milestone, goal, dream });
    });
    return result;
  }, [allScheduled, year]);

  return (
    <>
      <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto p-4 sm:grid-cols-2 lg:grid-cols-4">
        {months.map((month) => {
          const monthIso = dateToISO(new Date(month.getFullYear(), month.getMonth(), 1));
          const isDropping = dropTarget === monthIso;
          const isCurrent = isSameMonth(month, new Date());
          const items = itemsByMonth[month.getMonth()] || [];
          return (
            <div
              key={month.getMonth()}
              onDragOver={executeMode ? undefined : (event) => { event.preventDefault(); setDropTarget(monthIso); }}
              onDragLeave={executeMode ? undefined : () => setDropTarget((previous) => (previous === monthIso ? null : previous))}
              onDrop={executeMode ? undefined : (event) => onDrop(event, monthIso)}
              style={{
                borderRadius: 12,
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                minHeight: items.length > 0 && openMonth === month.getMonth() ? 140 : "auto",
                transition: "all 0.15s",
                border: isDropping
                  ? `1px solid ${colors.brandPrimary}`
                  : isCurrent
                    ? "2px solid rgba(0,136,255,0.25)"
                    : `1px solid ${colors.borderLight}`,
                background: isDropping ? "rgba(0,136,255,0.06)" : "#ffffff",
              }}
            >
              <button
                type="button"
                className="flex w-full items-center justify-between sm:cursor-default"
                style={{ background: "transparent", border: "none", padding: 0 }}
                onClick={() => setOpenMonth(openMonth === month.getMonth() ? -1 : month.getMonth())}
              >
                <div className="flex items-center gap-2">
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: isCurrent ? colors.brandPrimary : colors.textPrimary,
                    }}
                  >
                    {MONTH_NAMES[month.getMonth()]}
                  </span>
                  {isCurrent && (
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-semibold sm:hidden"
                      style={{ background: colors.brandPrimary, color: "#fff" }}
                    >
                      This Month
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {items.length > 0 && (
                    <span style={{ fontSize: 11, color: colors.textTertiary }}>{items.length}</span>
                  )}
                  <svg
                    className="flex-shrink-0 sm:hidden"
                    width="14" height="14" viewBox="0 0 14 14" fill="none"
                    style={{
                      transform: openMonth === month.getMonth() ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                      color: colors.textTertiary,
                    }}
                  >
                    <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
              {/* Mobile: colored dots for collapsed months with items */}
              {items.length > 0 && openMonth !== month.getMonth() && (
                <div className="flex flex-wrap gap-1 sm:hidden">
                  {items.slice(0, 8).map(({ dream, milestone }) => (
                    <span
                      key={milestone.id}
                      style={{
                        display: "inline-block",
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: (dream as any)?.color || colors.brandPrimary,
                        flexShrink: 0,
                      }}
                    />
                  ))}
                  {items.length > 8 && (
                    <span style={{ fontSize: 9, color: colors.textTertiary }}>+{items.length - 8}</span>
                  )}
                </div>
              )}
              <div
                className={openMonth === month.getMonth() ? "flex flex-col" : "hidden sm:flex sm:flex-col"}
                style={{ gap: 4, flex: 1 }}
              >
                {items.slice(0, expandedMonths[month.getMonth()] ? items.length : 4).map(({ milestone, goal, dream, iso }) => (
                  <div
                    key={milestone.id}
                    title={`${goal.title} — ${milestone.title}`}
                    draggable={!executeMode && !(milestone as any).completed}
                    onDragStart={executeMode || (milestone as any).completed ? undefined : (event) => onDragStart(event, milestone.id)}
                    onDragEnd={executeMode || (milestone as any).completed ? undefined : onDragEnd}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      background: (milestone as any).completed ? "rgba(0,0,0,0.03)" : "rgba(0,136,255,0.08)",
                      border: `1px solid ${(milestone as any).completed ? "rgba(0,0,0,0.08)" : "rgba(0,136,255,0.18)"}`,
                      borderRadius: 4,
                      padding: "2px 6px",
                      cursor: executeMode ? "pointer" : (milestone as any).completed ? "default" : "grab",
                      opacity: (milestone as any).completed ? 0.65 : 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        color: colors.textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {milestone.title}
                    </span>
                    <button
                      type="button"
                      onClick={() => onOpenMilestone({ iso: milestone.date || iso || "", dream, goal, milestone })}
                      title="View milestone details"
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: 20, height: 20, color: colors.textTertiary,
                        background: "#ffffff", border: `1px solid ${colors.borderLight}`,
                        cursor: "pointer", borderRadius: 6, flexShrink: 0,
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.brandPrimary; (e.currentTarget as HTMLButtonElement).style.background = "rgba(56,189,248,0.1)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textTertiary; (e.currentTarget as HTMLButtonElement).style.background = "#ffffff"; }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 13h6M9 17h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </button>
                    {(milestone as any).completed ? (
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#22c55e", flexShrink: 0 }}>✓</span>
                    ) : executeMode ? (
                      <button
                        type="button"
                        onClick={() => onMarkDone?.({ iso: milestone.date || iso || "", dream, goal, milestone })}
                        title="Mark as done"
                        style={{
                          fontSize: 10, fontWeight: 700, color: "#16a34a",
                          background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.25)",
                          cursor: "pointer", padding: "1px 4px", borderRadius: 4, flexShrink: 0, lineHeight: 1.4,
                        }}
                      >✓</button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onRemove(milestone.id)}
                        title="Remove from calendar"
                        style={{
                          fontSize: 11, fontWeight: 600, color: colors.textTertiary,
                          background: "transparent", border: "none", cursor: "pointer",
                          padding: "1px 3px", borderRadius: 4, flexShrink: 0, lineHeight: 1,
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#ef4444"; (e.currentTarget as HTMLButtonElement).style.background = "#fef2f2"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textTertiary; (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                      >✕</button>
                    )}
                  </div>
                ))}
                {items.length > 4 && expandedMonths[month.getMonth()] && (
                  <button
                    type="button"
                    className="sm:hidden cursor-pointer rounded border-0 bg-transparent px-1 py-0.5 text-left text-[11px] font-semibold"
                    style={{ color: colors.textTertiary }}
                    onClick={() => setExpandedMonths((prev) => ({ ...prev, [month.getMonth()]: false }))}
                  >
                    Show less
                  </button>
                )}
                {items.length > 4 && !expandedMonths[month.getMonth()] && (
                  <>
                    <button
                      type="button"
                      className="sm:hidden cursor-pointer rounded border-0 bg-transparent px-1 py-0.5 text-left text-[11px] font-semibold"
                      style={{ color: colors.brandPrimary }}
                      onClick={() => setExpandedMonths((prev) => ({ ...prev, [month.getMonth()]: true }))}
                    >
                      +{items.length - 4} more — tap to show all
                    </button>
                    <p className="hidden sm:block" style={{ margin: 0, fontSize: 10, color: colors.textTertiary }}>
                      +{items.length - 4} more
                    </p>
                  </>
                )}
                {!executeMode && isDropping && dragId && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      borderRadius: 4,
                      border: "2px dashed rgba(0,136,255,0.3)",
                      fontSize: 10,
                      color: "rgba(0,136,255,0.5)",
                      minHeight: 32,
                    }}
                  >
                    Drop here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default YearGrid;

